import { useState, useEffect } from "react";
import axios from "axios";
import { X, Play, ExternalLink } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Helper: convert YouTube/Vimeo URLs into embeddable URLs
const toEmbedUrl = (url) => {
  if (!url) return "";
  // YouTube
  let m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
  if (m) return `https://www.youtube.com/embed/${m[1]}?autoplay=1&rel=0`;
  // Vimeo
  m = url.match(/vimeo\.com\/(\d+)/);
  if (m) return `https://player.vimeo.com/video/${m[1]}?autoplay=1`;
  return url;
};

const isDirectVideo = (url) => /\.(mp4|webm|ogg|mov)(\?|$)/i.test(url || "");

/**
 * PromoVideoPopup — Shown to clients/affiliates on dashboard load
 * if the admin enabled an active promotional video.
 *
 * Props:
 *  - role: "client" | "affiliate"
 */
export default function PromoVideoPopup({ role = "client" }) {
  const [config, setConfig] = useState(null);
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/promo-video/active`, { params: { role } });
        if (!mounted) return;
        if (res.data && res.data.enabled) {
          // Show only once per video update per device
          const key = `promo_video_seen_${res.data.updated_at || ""}_${role}`;
          if (!localStorage.getItem(key)) {
            setConfig(res.data);
            setOpen(true);
          }
        }
      } catch (e) {
        if (process.env.NODE_ENV === "development") console.debug("[PromoVideo] fetch failed:", e?.message);
      }
    })();
    return () => { mounted = false; };
  }, [role]);

  const close = () => {
    if (config) {
      const key = `promo_video_seen_${config.updated_at || ""}_${role}`;
      try { localStorage.setItem(key, "1"); } catch (e) { if (process.env.NODE_ENV === "development") console.debug("[PromoVideo] localStorage failed:", e?.message); }
    }
    setOpen(false);
    setDismissed(true);
  };

  if (!open || !config || dismissed) return null;

  const embed = toEmbedUrl(config.video_url);
  const direct = isDirectVideo(config.video_url);

  return (
    <div
      onClick={close}
      data-testid="promo-video-overlay"
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", zIndex: 9998,
        display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="promo-video-modal"
        style={{
          background: "linear-gradient(135deg, #0F172A 0%, #1E293B 100%)",
          color: "white",
          borderRadius: 14,
          padding: "1rem 1rem 1.2rem",
          width: "min(720px, 95vw)",
          maxHeight: "92vh",
          overflowY: "auto",
          border: "1px solid #334155",
          boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.6rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ background: "#EF4444", padding: "4px 10px", borderRadius: 999, fontSize: "0.7rem", fontWeight: 800, letterSpacing: 0.5 }}>
              ● EN DIRECT
            </div>
            <div style={{ fontSize: "0.78rem", color: "#94A3B8" }}>SMARTECH SYSTEM PRO</div>
          </div>
          <button
            onClick={close}
            data-testid="promo-video-close"
            style={{
              background: "rgba(255,255,255,0.1)", border: "none", color: "white",
              borderRadius: 8, padding: 6, cursor: "pointer",
            }}
            aria-label="Fermer"
          >
            <X size={18} />
          </button>
        </div>

        {config.title && (
          <h2 style={{ fontSize: "1.3rem", fontWeight: 800, margin: "0 0 0.4rem" }} data-testid="promo-video-title">
            {config.title}
          </h2>
        )}
        {config.message && (
          <p style={{ fontSize: "0.92rem", color: "#CBD5E1", margin: "0 0 0.8rem", lineHeight: 1.45, whiteSpace: "pre-line" }}>
            {config.message}
          </p>
        )}

        {/* Video player */}
        {config.video_url && (
          <div style={{ position: "relative", paddingTop: "56.25%", borderRadius: 10, overflow: "hidden", background: "#000", marginBottom: "0.7rem" }}>
            {direct ? (
              <video
                controls
                autoPlay
                playsInline
                src={config.video_url}
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
                data-testid="promo-video-player"
              />
            ) : (
              <iframe
                title="Promo video"
                src={embed}
                allow="autoplay; encrypted-media; picture-in-picture"
                allowFullScreen
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
                data-testid="promo-video-iframe"
              />
            )}
          </div>
        )}

        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", justifyContent: "flex-end" }}>
          {config.cta_label && config.cta_url && (
            <a
              href={config.cta_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="promo-video-cta"
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                background: "#16A34A", color: "white", padding: "0.55rem 1rem",
                borderRadius: 10, fontWeight: 700, textDecoration: "none", fontSize: "0.85rem",
              }}
              onClick={() => {
                // Auto-close on CTA click
                setTimeout(close, 200);
              }}
            >
              <Play size={14} /> {config.cta_label} <ExternalLink size={12} />
            </a>
          )}
          <button
            onClick={close}
            data-testid="promo-video-dismiss"
            style={{
              background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)",
              color: "white", padding: "0.55rem 1rem", borderRadius: 10, fontWeight: 600,
              fontSize: "0.85rem", cursor: "pointer",
            }}
          >
            Plus tard
          </button>
        </div>
      </div>
    </div>
  );
}
